import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries, HistogramSeries } from 'lightweight-charts';

const TechnicalIndicatorsChart = ({ candleData, analysis }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const seriesRef = useRef(null);
  const macdSeriesRefs = useRef({}); // Store refs for all three MACD series
  const [selectedIndicator, setSelectedIndicator] = useState('rsi'); // 'rsi', 'macd', 'volume'

  // Calculate indicators
  const calculateRSI = (data, period = 14) => {
    if (!data || !Array.isArray(data) || data.length < period + 1) {
      return [];
    }
    
    // Extract closes with original indices - handle both formats
    const closesWithIndex = [];
    for (let idx = 0; idx < data.length; idx++) {
      const d = data[idx];
      if (!d) continue;
      
      const close = parseFloat(d.c || d.close || 0);
      if (isNaN(close) || close <= 0) continue;
      
      const time = d.t ? Math.floor(d.t / 1000) : Math.floor(Date.now() / 1000) - (data.length - idx) * 86400;
      closesWithIndex.push({
        close: close,
        index: idx,
        time: time
      });
    }
    
    if (closesWithIndex.length < period + 1) {
      return [];
    }
    
    const rsiData = [];
    let gains = 0;
    let losses = 0;
    
    // Calculate initial average gain/loss
    for (let i = 1; i <= period && i < closesWithIndex.length; i++) {
      const change = closesWithIndex[i].close - closesWithIndex[i - 1].close;
      if (change > 0) {
        gains += change;
      } else {
        losses += Math.abs(change);
      }
    }
    
    if (period === 0) return [];
    
    let avgGain = gains / period;
    let avgLoss = losses / period;
    
    // Ensure we have valid averages
    if (isNaN(avgGain)) avgGain = 0;
    if (isNaN(avgLoss)) avgLoss = 0.0001; // Avoid division by zero
    
    // Calculate RSI for first point
    let rs = 0;
    if (avgLoss === 0) {
      rs = avgGain > 0 ? 100 : 0;
    } else {
      rs = avgGain / avgLoss;
    }
    
    let rsi = 50; // Default
    if (isFinite(rs) && !isNaN(rs)) {
      rsi = 100 - (100 / (1 + rs));
      rsi = Math.max(0, Math.min(100, rsi));
    }
    
    if (isFinite(rsi) && !isNaN(rsi)) {
      rsiData.push({
        time: closesWithIndex[period].time,
        value: rsi
      });
    }
    
    // Calculate RSI for remaining points
    for (let i = period + 1; i < closesWithIndex.length; i++) {
      const change = closesWithIndex[i].close - closesWithIndex[i - 1].close;
      const gain = change > 0 ? change : 0;
      const loss = change < 0 ? Math.abs(change) : 0;
      
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
      
      // Ensure valid averages
      if (isNaN(avgGain)) avgGain = 0;
      if (isNaN(avgLoss)) avgLoss = 0.0001;
      
      if (avgLoss === 0) {
        rs = avgGain > 0 ? 100 : 0;
      } else {
        rs = avgGain / avgLoss;
      }
      
      rsi = 50; // Default
      if (isFinite(rs) && !isNaN(rs)) {
        rsi = 100 - (100 / (1 + rs));
        rsi = Math.max(0, Math.min(100, rsi));
      }
      
      if (isFinite(rsi) && !isNaN(rsi)) {
        rsiData.push({
          time: closesWithIndex[i].time,
          value: rsi
        });
      }
    }
    
    // Final filter - ensure all values are valid
    return rsiData.filter(d => {
      const valid = d && 
                    typeof d.value === 'number' && 
                    !isNaN(d.value) && 
                    isFinite(d.value) && 
                    d.value >= 0 && 
                    d.value <= 100 &&
                    typeof d.time === 'number' &&
                    !isNaN(d.time);
      if (!valid && d) {
        console.warn('Filtered invalid RSI point:', d);
      }
      return valid;
    });
  };

  const calculateMACD = (data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
    if (!data || data.length < slowPeriod) return [];
    
    // Calculate EMAs
    const calculateEMA = (values, period) => {
      const multiplier = 2 / (period + 1);
      let ema = values.slice(0, period).reduce((sum, val) => sum + val, 0) / period;
      const emaValues = [ema];
      
      for (let i = period; i < values.length; i++) {
        ema = (values[i] - ema) * multiplier + ema;
        emaValues.push(ema);
      }
      
      return emaValues;
    };
    
    const closes = data.map(d => parseFloat(d.c || d.close || 0));
    const fastEMA = calculateEMA(closes, fastPeriod);
    const slowEMA = calculateEMA(closes, slowPeriod);
    
    // Calculate MACD line
    const macdLine = [];
    const startIdx = slowPeriod - fastPeriod;
    for (let i = 0; i < slowEMA.length; i++) {
      macdLine.push(fastEMA[i + startIdx] - slowEMA[i]);
    }
    
    // Calculate signal line (EMA of MACD)
    const signalLine = calculateEMA(macdLine, signalPeriod);
    
    // Calculate histogram (MACD - Signal)
    const histogram = [];
    const signalStartIdx = macdLine.length - signalLine.length;
    for (let i = 0; i < signalLine.length; i++) {
      histogram.push(macdLine[i + signalStartIdx] - signalLine[i]);
    }
    
    // Return MACD values aligned with data
    const macdData = [];
    const dataStartIdx = slowPeriod - 1;
    const histogramStartIdx = histogram.length - 1;
    
    for (let i = 0; i < histogram.length; i++) {
      const dataIdx = dataStartIdx + signalStartIdx + i;
      if (dataIdx < data.length) {
        macdData.push({
          time: data[dataIdx].t ? Math.floor(data[dataIdx].t / 1000) : Math.floor(Date.now() / 1000),
          value: macdLine[i + signalStartIdx] // Use MACD line value
        });
      }
    }
    
    return macdData;
  };

  const calculateEMA = (data, period) => {
    if (!data || data.length < period) return [];
    
    const closes = data.map(d => parseFloat(d.c || d.close || 0));
    const multiplier = 2 / (period + 1);
    let ema = closes.slice(0, period).reduce((sum, val) => sum + val, 0) / period;
    const emaData = [{
      time: data[period - 1].t ? Math.floor(data[period - 1].t / 1000) : Math.floor(Date.now() / 1000),
      value: ema
    }];
    
    for (let i = period; i < data.length; i++) {
      ema = (closes[i] - ema) * multiplier + ema;
      emaData.push({
        time: data[i].t ? Math.floor(data[i].t / 1000) : Math.floor(Date.now() / 1000),
        value: ema
      });
    }
    
    return emaData;
  };

  // Main chart effect
  useEffect(() => {
    if (!chartRef.current || !candleData || candleData.length === 0) return;

    const container = chartRef.current;
    const containerWidth = container.clientWidth || container.offsetWidth;
    
    if (!containerWidth || containerWidth === 0) {
      requestAnimationFrame(() => {
        // Retry on next frame
      });
      return;
    }

    if (chartInstanceRef.current) {
      chartInstanceRef.current.remove();
      chartInstanceRef.current = null;
      seriesRef.current = null;
    }

    const finalWidth = Math.max(containerWidth, 100);
    const finalHeight = 300;

    const chart = createChart(container, {
      width: finalWidth,
      height: finalHeight,
      layout: {
        backgroundColor: '#ffffff',
        textColor: '#333',
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
      rightPriceScale: {
        borderColor: '#cccccc',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderColor: '#cccccc',
      },
    });
    
    if (!chart) {
      console.error('Failed to create chart instance');
      return;
    }

    chart.resize(finalWidth, finalHeight);
    chartInstanceRef.current = chart;

    const timeoutId = setTimeout(() => {
      try {
        if (!chartInstanceRef.current || !container.isConnected) {
          console.error('Chart or container no longer valid');
          return;
        }

        if (!chart || typeof chart.addSeries !== 'function') {
          console.error('Chart not ready - addSeries not available');
          return;
        }

        const rect = container.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          console.error('Container has no dimensions:', rect);
          return;
        }

        let indicatorData = [];
        let seriesColor = '#26a69a';
        let seriesType = LineSeries;
        let seriesOptions = { lineWidth: 2 };

        // Prepare data based on selected indicator
        if (selectedIndicator === 'rsi') {
          indicatorData = calculateRSI(candleData);
          if (indicatorData.length === 0 && candleData && candleData.length > 0) {
            console.warn('RSI calculation returned empty array. Data length:', candleData.length);
            console.warn('Sample data:', candleData.slice(0, 3));
          }
          const currentRSI = analysis?.indicators?.rsi || 50;
          seriesColor = currentRSI < 30 ? '#26a69a' : currentRSI > 70 ? '#ef5350' : '#ffa726';
          seriesOptions = { color: seriesColor, lineWidth: 2 };
        } else if (selectedIndicator === 'macd') {
          // For MACD, calculate all three EMAs (50, 100, 200) and add them as separate series
          const ema50 = calculateEMA(candleData, 50);
          const ema100 = calculateEMA(candleData, 100);
          const ema200 = calculateEMA(candleData, 200);
          
          if (ema50.length === 0 && ema100.length === 0 && ema200.length === 0 && candleData && candleData.length > 0) {
            console.warn('All EMA calculations returned empty arrays. Data length:', candleData.length);
          }
          
          // Add EMA 50 series
          if (ema50.length > 0) {
            const validEma50 = ema50.filter(d => !isNaN(d.value) && isFinite(d.value) && d.value > 0);
            if (validEma50.length > 0) {
              const ema50Series = chart.addSeries(LineSeries, {
                color: '#2196F3', // Blue
                lineWidth: 2,
                title: 'EMA 50',
              });
              ema50Series.setData(validEma50);
              macdSeriesRefs.current.ema50 = ema50Series;
            }
          }
          
          // Add EMA 100 series
          if (ema100.length > 0) {
            const validEma100 = ema100.filter(d => !isNaN(d.value) && isFinite(d.value) && d.value > 0);
            if (validEma100.length > 0) {
              const ema100Series = chart.addSeries(LineSeries, {
                color: '#FF9800', // Orange
                lineWidth: 2,
                title: 'EMA 100',
              });
              ema100Series.setData(validEma100);
              macdSeriesRefs.current.ema100 = ema100Series;
            }
          }
          
          // Add EMA 200 series
          if (ema200.length > 0) {
            const validEma200 = ema200.filter(d => !isNaN(d.value) && isFinite(d.value) && d.value > 0);
            if (validEma200.length > 0) {
              const ema200Series = chart.addSeries(LineSeries, {
                color: '#9C27B0', // Purple
                lineWidth: 2,
                title: 'EMA 200',
              });
              ema200Series.setData(validEma200);
              macdSeriesRefs.current.ema200 = ema200Series;
            }
          }
          
          // Set indicatorData to empty since we've already added the series
          indicatorData = [];
          seriesType = null; // No single series for MACD
        } else if (selectedIndicator === 'volume') {
          // Volume data
          indicatorData = candleData
            .map((candle, index) => {
              const close = parseFloat(candle.c || candle.close || 0);
              const open = parseFloat(candle.o || candle.open || 0);
              const volume = parseFloat(candle.v || candle.volume || 0);
              
              if (isNaN(volume) || volume <= 0) return null;
              
              return {
                time: candle.t ? Math.floor(candle.t / 1000) : Math.floor(Date.now() / 1000) - (candleData.length - index) * 86400,
                value: volume,
                color: close >= open ? '#26a69a' : '#ef5350',
              };
            })
            .filter(v => v !== null && v.value > 0 && isFinite(v.value))
            .sort((a, b) => a.time - b.time)
            .filter((item, index, arr) => index === 0 || item.time !== arr[index - 1].time);
          
          seriesType = HistogramSeries;
          seriesOptions = {
            color: '#26a69a',
            priceFormat: {
              type: 'volume',
            },
            priceScaleId: '',
            scaleMargins: {
              top: 0.8,
              bottom: 0,
            },
          };
        }

        // For MACD, we've already added all series above, so skip the single series logic
        if (selectedIndicator === 'macd') {
          // MACD series already added above
          chart.timeScale().fitContent();
          return;
        }

        if (indicatorData.length === 0) {
          console.error('No indicator data available');
          return;
        }

        // Filter out any invalid data points
        const validData = indicatorData.filter(d => {
          if (!d || isNaN(d.value) || !isFinite(d.value)) return false;
          if (selectedIndicator === 'volume') {
            return d.value > 0;
          } else if (selectedIndicator === 'rsi') {
            return d.value >= 0 && d.value <= 100;
          } else {
            return true; // MACD/EMA can be any finite number
          }
        });

        if (validData.length === 0) {
          console.error('No valid indicator data after filtering');
          return;
        }

        // Add series
        const series = chart.addSeries(seriesType, seriesOptions);
        seriesRef.current = series;

        // Set data
        series.setData(validData);

        // Add reference lines for RSI
        if (selectedIndicator === 'rsi') {
          series.createPriceLine({
            price: 70,
            color: '#ef5350',
            lineWidth: 1,
            lineStyle: 2, // Dashed
            axisLabelVisible: true,
            title: 'Overbought (70)',
          });

          series.createPriceLine({
            price: 30,
            color: '#26a69a',
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: 'Oversold (30)',
          });

          series.createPriceLine({
            price: 50,
            color: '#ffa726',
            lineWidth: 1,
            lineStyle: 3, // Dotted
            axisLabelVisible: true,
            title: 'Neutral (50)',
          });
        }

        // Note: MACD series already added above with individual series

        chart.timeScale().fitContent();
      } catch (error) {
        console.error(`Error adding ${selectedIndicator} series:`, error);
        console.error('Chart state:', { 
          chart: !!chart, 
          container: container.isConnected, 
          width: container.clientWidth,
          height: container.clientHeight,
          rect: container.getBoundingClientRect()
        });
      }
    }, 200);

    return () => {
      // Clear timeout if component unmounts
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.remove();
        } catch (e) {
          // Chart already removed or invalid
        }
        chartInstanceRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [candleData, analysis, selectedIndicator]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Technical Indicators</h4>
        
        {/* Indicator Selector */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Indicator:</label>
            <select
              value={selectedIndicator}
              onChange={(e) => setSelectedIndicator(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="rsi">RSI</option>
              <option value="macd">MACD (EMA)</option>
              <option value="volume">Volume</option>
            </select>
          </div>

          {/* MACD Legend (only show for MACD) */}
          {selectedIndicator === 'macd' && (
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 bg-blue-600"></div>
                <span className="text-gray-700">EMA 50</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 bg-orange-500"></div>
                <span className="text-gray-700">EMA 100</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 bg-purple-600"></div>
                <span className="text-gray-700">EMA 200</span>
              </div>
            </div>
          )}
        </div>

        {/* Current Value Display */}
        <div className="mb-4">
          {selectedIndicator === 'rsi' && analysis?.indicators?.rsi && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Current RSI: <strong className="text-gray-900">{analysis.indicators.rsi.toFixed(2)}</strong>
              </span>
              <span className={`text-sm font-medium ${
                analysis.indicators.rsi < 30 ? 'text-green-600' :
                analysis.indicators.rsi > 70 ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {analysis.indicators.rsi < 30 ? 'Oversold' :
                 analysis.indicators.rsi > 70 ? 'Overbought' : 'Neutral'}
              </span>
            </div>
          )}
          {selectedIndicator === 'macd' && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing all three EMAs: <strong className="text-gray-900">50, 100, 200</strong>
              </span>
              <span className="text-sm text-gray-500">
                All periods displayed simultaneously
              </span>
            </div>
          )}
          {selectedIndicator === 'volume' && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Volume: <strong className="text-gray-900">
                  {candleData.length > 0 ? 
                    (parseFloat(candleData[candleData.length - 1]?.v || candleData[candleData.length - 1]?.volume || 0) / 1000000).toFixed(2) + 'M' 
                    : 'N/A'}
                </strong>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Chart Container */}
      <div ref={chartRef} className="w-full" style={{ height: '300px' }} />
    </div>
  );
};

export default TechnicalIndicatorsChart;
